import { DEFAULT_NEXT_STEP_DEFERRED_DELAY_MS, FormStepper, type FormStepperProps, useFormStepper } from '@1trading/react/components/ui/form-stepper';
import { type Cancellable, cancellableSleep } from '@1trading/react/lib/cancellable';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import type { FieldValues } from 'react-hook-form';

import { useFormStepGuard } from '@/hooks/use-form-step-guard';
import type { FormStepComponent, FormStepDirection } from '@/types/components';

type GuardedFormStepperContextType<T extends FieldValues = FieldValues> = {
  onSubmit: FormStepperProps<T>['onSubmit'];
  steps: FormStepComponent<T>[];
};

const GuardedFormStepperContext = createContext<GuardedFormStepperContextType | undefined>(undefined);

interface GuardedFormStepperProps<T extends FieldValues = FieldValues> extends FormStepperProps<T> {
  initialStep?: number;
  steps: FormStepComponent<T>[];
}

// TODO: Replace with an initialStep prop on FormStepper in @1trading/react once accounts-ui is on its latest version
const GuardedFormStepperInitialStep = ({ step }: { step: number }) => {
  const { setStep } = useFormStepper();

  useLayoutEffect(() => {
    setStep(step);
  }, [setStep, step]);

  return null;
};
GuardedFormStepperInitialStep.displayName = 'GuardedFormStepperInitialStep';

export const GuardedFormStepper = <T extends FieldValues = FieldValues>({ children, initialStep, steps, ...formStepperProps }: GuardedFormStepperProps<T>) => {
  return (
    <GuardedFormStepperContext.Provider
      value={{
        onSubmit: formStepperProps.onSubmit as FormStepperProps<FieldValues>['onSubmit'],
        steps: steps as FormStepComponent<FieldValues>[],
      }}
    >
      <FormStepper {...formStepperProps}>
        {initialStep ? <GuardedFormStepperInitialStep step={initialStep} /> : null}
        {children}
      </FormStepper>
    </GuardedFormStepperContext.Provider>
  );
};

export const useGuardedFormStepper = <T extends FieldValues = FieldValues>() => {
  const context = useContext(GuardedFormStepperContext) as GuardedFormStepperContextType<T> | undefined;
  if (!context) throw new Error('useGuardedFormStepper must be used within a GuardedFormStepperProvider');

  const { onSubmit, steps } = context;
  const { nextStep, nextStepDeferred, prevStep, ...formStepper } = useFormStepper();
  const { currentStep } = formStepper;
  const { executeGuard } = useFormStepGuard<T>({ onSubmit, steps });
  const nextStepDeferredCancellableRef = useRef<Cancellable<void>>();

  const executeStepGuards = useCallback(
    async (direction: FormStepDirection) => {
      const currentStepFieldset = steps[currentStep];
      const targetStepFieldset = steps[currentStep + (direction === 'next' ? 1 : -1)];

      if (currentStepFieldset?.beforeStepLeave) {
        const canProceed = await executeGuard(currentStepFieldset.beforeStepLeave, direction);
        if (!canProceed) return false;
      }

      if (targetStepFieldset?.beforeStepEnter) {
        const canProceed = await executeGuard(targetStepFieldset.beforeStepEnter, direction);
        if (!canProceed) return false;
      }

      return true;
    },
    [currentStep, executeGuard, steps],
  );

  const handleNextStep: typeof nextStep = useCallback(
    async (options) => {
      if (await executeStepGuards('next')) {
        await nextStep(options);
      }
    },
    [executeStepGuards, nextStep],
  );

  const handleNextStepDeferred: typeof nextStepDeferred = useCallback(
    async ({ delayMs = DEFAULT_NEXT_STEP_DEFERRED_DELAY_MS, ...options } = {}) => {
      if (nextStepDeferredCancellableRef.current) return;

      nextStepDeferredCancellableRef.current = cancellableSleep(delayMs);

      try {
        await nextStepDeferredCancellableRef.current.promise;
        await handleNextStep(options);
      } catch {
        return;
      } finally {
        nextStepDeferredCancellableRef.current = undefined;
      }
    },
    [handleNextStep],
  );

  const handlePrevStep = useCallback(async () => {
    if (await executeStepGuards('prev')) prevStep();
  }, [executeStepGuards, prevStep]);

  useEffect(() => {
    return () => {
      if (nextStepDeferredCancellableRef.current) nextStepDeferredCancellableRef.current.cancel();
    };
  }, []);

  return {
    ...formStepper,
    nextStep: handleNextStep,
    nextStepDeferred: handleNextStepDeferred,
    prevStep: handlePrevStep,
  };
};
